import React from 'react';
import PropTypes from 'prop-types';
import DPicker from 'react-datepicker';
import moment from 'moment';
import { Input } from '@chakra-ui/react';
import Constants from '../../Constants';
import { getDate } from 'date-fns';

import 'react-datepicker/dist/react-datepicker.css';

function DatePicker(props) {
  const {
    closeOnSelect,
    dateFormat,
    timeFormat,
    id,
    inputProps,
    onChange,
    value,
    timeConstraints,
    onBlur,
    filterDate,
    placeholder,
    ...rest
  } = props;

  const dateFormatStr = dateFormat || Constants.DEFAULT_READ_DATE_FORMAT;

  const getDateObject = val => {
    if (!val) return null;

    if (moment.isMoment(val) && val.isValid()) {
      return val.toDate();
    }

    if (val instanceof Date && !isNaN(val.getTime())) {
      return val;
    }

    if (typeof val === 'string') {
      const parsed = moment(val, dateFormatStr, true);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }

    return null;
  };

  const handleChange = date => {
    let val = date;
    if (!date || isNaN(date.getTime())) {
      if (typeof onChange === 'function') onChange(null);
      return;
    }

    if ((date && typeof date === 'string' && date.length > 9) || !isNaN(date.getTime())) {
      val = moment(date, dateFormatStr, true);
    }

    // eslint-disable-next-line no-underscore-dangle
    const formattedVal = val?._isValid ? val.format(typeof timeFormat === 'string' ? timeFormat : dateFormatStr) : date;

    if (typeof onChange === 'function') onChange(formattedVal);
  };

  return (
    <DPicker
      showMonthDropdown
      showYearDropdown
      isClearable
      {...{
        id,
        closeOnSelect: closeOnSelect || true,
        onBlur: onBlur && typeof onBlur === 'function' ? onBlur : () => true,
        placeholderText: placeholder || inputProps?.placeholder || 'Select Date',
        ...inputProps,
        ...rest,
        dateFormat: 'yyyy-MM-dd',
      }}
      selected={getDateObject(value) || null}
      onChange={handleChange}
      filterDate={filterDate}
      customInput={<Input autoFocus={false} />}
    />
  );
}

export const SHIPMENT_CONFIG = {
  ALLOWED_START_DAYS: [1,16],
  ALLOWED_END_DAYS: [15],
  ALLOW_LAST_DAY_OF_MONTH: true,
};

function toLocalDate(value) {
    if (value instanceof Date) return value;
    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : new Date(value);
}

export function isStartDate(date,restrictShipmentDates){
    if(!date) return false;
    if(!restrictShipmentDates) return true;
    const day = getDate(toLocalDate(date));
    return SHIPMENT_CONFIG.ALLOWED_START_DAYS.includes(day);
}

export function isValidEndDate(date,restrictShipmentDates){
    if(!date) return false;
    if(!restrictShipmentDates) return true;
    const da = toLocalDate(date);
    const day = da.getDate();
    const month = da.getMonth();
    const year = da.getFullYear();

    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    if(day > lastDayOfMonth) return false;

    const isAllowedEndDate = SHIPMENT_CONFIG.ALLOWED_END_DAYS.includes(day);
    const isLastDate = SHIPMENT_CONFIG.ALLOW_LAST_DAY_OF_MONTH && day === lastDayOfMonth;

    return isAllowedEndDate || isLastDate;
}

export function getShipmentWindowEndDate(startDate) {
    const start = toLocalDate(startDate);
    const year = start.getFullYear();
    const month = start.getMonth();
    const day = start.getDate();

    if (day === 1) {
        return new Date(year, month, 15);
    }

    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, lastDayOfMonth);
}

DatePicker.propTypes = {
  closeOnSelect: PropTypes.bool,
  dateFormat: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputProps: PropTypes.shape({
    borderColor: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  timeFormat: PropTypes.string,
  timeConstraints: PropTypes.shape({
    hours: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
};

export default DatePicker;
